import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, ArrowRight, Package, Bell, CheckCircle, Users
} from 'lucide-react';
import type { UserRole } from '../../types/profiles';

interface OnboardingGuideProps {
  role: UserRole;
  companyName: string | null;
  onClose: () => void;
}

const ROLE_STEPS: Record<string, { icon: React.ElementType; title: string; description: string; action: string; link: string }[]> = {
  shipper: [
    {
      icon: Package,
      title: 'Post a Job',
      description: 'Create a job listing with details about the position and location.',
      action: 'Browse Job Seekers',
      link: '/job-seekers',
    },
    {
      icon: Users,
      title: 'Review Applicants',
      description: 'Browse job seeker profiles and find the right fit.',
      action: 'View Job Seekers',
      link: '/job-seekers',
    },
    {
      icon: CheckCircle,
      title: 'Set Up Your Profile',
      description: 'Complete your company profile to attract top talent.',
      action: 'Go to Profile',
      link: '/profile',
    },
  ],
  carrier: [
    {
      icon: Users,
      title: 'Set Up Your Profile',
      description: 'Add your experience, skills, and preferred job types.',
      action: 'Go to Profile',
      link: '/profile',
    },
    {
      icon: Package,
      title: 'Browse Available Jobs',
      description: 'Find ranch and agriculture jobs that match your skills.',
      action: 'View Jobs',
      link: '/job-seekers',
    },
    {
      icon: Bell,
      title: 'Set Up Job Alerts',
      description: 'Get notified when jobs matching your preferences are posted.',
      action: 'Set Up Alerts',
      link: '/profile',
    },
  ],
  broker: [
    {
      icon: Package,
      title: 'Post Jobs for Clients',
      description: 'Create job postings on behalf of your clients.',
      action: 'Browse Job Seekers',
      link: '/job-seekers',
    },
    {
      icon: Users,
      title: 'Match Workers',
      description: 'Connect qualified workers with the right opportunities.',
      action: 'View Job Seekers',
      link: '/job-seekers',
    },
    {
      icon: CheckCircle,
      title: 'Complete Your Profile',
      description: 'Set up your broker profile to manage placements.',
      action: 'Go to Profile',
      link: '/profile',
    },
  ],
};

export default function OnboardingGuide({ role, companyName, onClose }: OnboardingGuideProps) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ROLE_STEPS[role] || ROLE_STEPS.shipper;

  const handleAction = () => {
    navigate(steps[currentStep].link);
    onClose();
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const step = steps[currentStep];
  const Icon = step.icon;
  const roleLabel = role === 'carrier' ? 'Carrier' : role === 'broker' ? 'Broker' : 'Shipper';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-fadeIn overflow-hidden">
        <div className="bg-green-600 p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-6 w-6" />
              <span className="font-semibold">Account Created</span>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-green-500 rounded-lg transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>
          <h2 className="text-2xl font-bold">
            Welcome{companyName ? `, ${companyName}` : ''}!
          </h2>
          <p className="text-green-100 mt-1">
            Here is how to get started as a {roleLabel}.
          </p>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  idx <= currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-green-50 rounded-xl flex-shrink-0">
              <Icon className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-green-600 font-semibold uppercase tracking-wide mb-1">
                Step {currentStep + 1} of {steps.length}
              </p>
              <h3 className="text-lg font-bold text-gray-900">{step.title}</h3>
              <p className="text-gray-600 mt-1">{step.description}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleAction}
              className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
            >
              {step.action}
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={handleNext}
              className="px-5 py-3 border border-gray-300 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors"
            >
              {currentStep < steps.length - 1 ? 'Next' : 'Done'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
